import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { v4 as UUID } from 'uuid';
import { useBackend } from '../../../Hooks/useBackend';
import { LocalizedValue, createLocalizedValue } from '../../LocalizedValue';
import { RecipeIngredient, createRecipeIngredient } from './RecipeIngredient';
import { NutritionInformation, createNutritionInformation } from '../Ingredient/NutritionInformation';
import { RecipeInstruction, createRecipeInstruction } from './RecipeInstruction';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { RecipeSearchParams, SeasonType } from './RecipeSearchParams';
import { Pageable } from '../../Interfaces/Pageable';
import { Media, createMedia } from '../../Media/Media';
import { LocalizedEntity, createLocalizedEntity } from '../../LocalizedEntity';

/**
 * Created by neo on 18.12.20.
 */

export type Recipe = LocalizedEntity & {
  veryHealthy?: boolean;
  cheap?: boolean;
  veryPopular?: boolean;
  sustainable?: boolean;
  weightWatcherSmartPoints: number;
  aggregateLikes: number;
  healthScore: number;
  creditsText?: string;
  license?: string;
  sourceName?: string;
  pricePerServing: number;
  ingredients: RecipeIngredient[];
  readyInMinutes: number;
  estimatedReadingMinutes: number;
  servings: number;
  sourceUrl?: string;
  image?: Media;
  nutrition?: NutritionInformation;
  summary: LocalizedValue[];
  cuisines: string[];
  dishTypes: string[];
  diets: string[];
  occasions: string[];
  seasons: SeasonType[];
  instructions: RecipeInstruction[];
  spoonacularSourceUrl?: string;
  favorite: boolean;
  readonly type: 'recipe';
  readonly media: Media | undefined;
  readonly durationInMinutes: number;
  calculate(kcal: number): Recipe;
  addFavorite(): Promise<Recipe>;
  removeFavorite(): Promise<Recipe>;
  getImage(lang: string): Media | undefined;
};

export function createRecipe(json?: Partial<Recipe>): Recipe {
  const base = createLocalizedEntity(json as any);
  const recipe: Recipe = makeAutoObservable({
    ...base,
    id: json?.id ?? UUID(),
    veryHealthy: json?.veryHealthy,
    cheap: json?.cheap,
    veryPopular: json?.veryPopular,
    sustainable: json?.sustainable,
    weightWatcherSmartPoints: json?.weightWatcherSmartPoints ?? 0,
    aggregateLikes: json?.aggregateLikes ?? 0,
    healthScore: json?.healthScore ?? 0,
    creditsText: json?.creditsText,
    license: json?.license,
    sourceName: json?.sourceName,
    pricePerServing: json?.pricePerServing ?? 0,
    ingredients: ((json?.ingredients as any) ?? []).map((i: any) => createRecipeIngredient(i)),
    readyInMinutes: json?.readyInMinutes ?? 0,
    estimatedReadingMinutes: json?.estimatedReadingMinutes ?? 1,
    servings: json?.servings ?? 1,
    sourceUrl: json?.sourceUrl,
    image: json?.image ? createMedia(json.image as any) : undefined,
    nutrition: createNutritionInformation(json?.nutrition as any),
    summary: ((json?.summary as any) ?? []).map((l: any) => createLocalizedValue(l)),
    cuisines: json?.cuisines ?? [],
    dishTypes: json?.dishTypes ?? [],
    diets: json?.diets ?? [],
    occasions: json?.occasions ?? [],
    seasons: json?.seasons ?? [],
    instructions: ((json?.instructions as any) ?? []).map((i: any) => createRecipeInstruction(i)),
    spoonacularSourceUrl: json?.spoonacularSourceUrl,
    favorite: json?.favorite ?? false,
    type: 'recipe' as const,

    get media(): Media | undefined {
      return this.image;
    },

    get durationInMinutes(): number {
      return this.readyInMinutes;
    },

    calculate(kcal: number): Recipe {
      return this;
    },

    addFavorite(): Promise<Recipe> {
      return HttpBackend.post(`/coach-program/diet/recipes/favorite`, [this.id]).then(() => {
        runInAction(() => (this.favorite = true));
        return this;
      });
    },

    removeFavorite(): Promise<Recipe> {
      return HttpBackend.delete(`/coach-program/diet/recipes/favorite/${this.id}`).then(() => {
        runInAction(() => (this.favorite = false));
        return this;
      });
    },

    getImage(lang: string): Media | undefined {
      return this.image;
    },
  });

  return recipe;
}

function transformRecipeSearchParams(params?: RecipeSearchParams) {
  return Object.assign({}, params, {
    dishTypes: params?.dishTypes?.join(','),
    cuisine: params?.cuisine?.join(','),
    diet: params?.diet?.join(','),
    seasons: params?.seasons?.join(','),
  });
}

// Hooks
export function useRecipes(params?: RecipeSearchParams) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['recipes', params],
    queryFn: () =>
      backend
        .get('/coach-program/diet/recipes', transformRecipeSearchParams(params))
        .then((res) => (res ?? []).map(createRecipe)),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRandomRecipes(params?: RecipeSearchParams) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['randomRecipes', params],
    queryFn: () =>
      backend
        .get('/coach-program/diet/recipes/random', transformRecipeSearchParams(params))
        .then((res) => (res ?? []).map(createRecipe)),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecipe(id: string | undefined) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['recipe', id],
    queryFn: () => backend.get(`/coach-program/diet/recipes/${id}`).then((res) => createRecipe(res)),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFavoriteRecipes(params?: Pageable) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['favoriteRecipes', params],
    queryFn: () =>
      backend.get('/coach-program/diet/recipes/favorite', params).then((res) => (res ?? []).map(createRecipe)),
    staleTime: 2 * 60 * 1000,
  });
}

export function useAddFavoriteRecipe() {
  const backend = useBackend();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: string) => backend.post('/coach-program/diet/recipes/favorite', [recipeId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteRecipes'] });
    },
  });
}

export function useRemoveFavoriteRecipe() {
  const backend = useBackend();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: string) => backend.delete(`/coach-program/diet/recipes/favorite/${recipeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteRecipes'] });
    },
  });
}

// Legacy static API - prefer hooks above
export const RecipeApi = {
  search(params?: RecipeSearchParams): Promise<Recipe[]> {
    return HttpBackend.get(`/coach-program/diet/recipes`, transformRecipeSearchParams(params)).then((result) =>
      result.map((r: any) => createRecipe(r)),
    );
  },

  random(params?: RecipeSearchParams): Promise<Recipe[]> {
    return HttpBackend.get(`/coach-program/diet/recipes/random`, transformRecipeSearchParams(params)).then((result) =>
      result.map((r: any) => createRecipe(r)),
    );
  },

  get(id: string): Promise<Recipe> {
    return HttpBackend.get(`/coach-program/diet/recipes/${id}`).then((result) => createRecipe(result));
  },

  count(params?: any): Promise<number> {
    return HttpBackend.get(`/coach-program/diet/recipes/count`, params).then((result) => result ?? 0);
  },

  findFavorites(params?: Pageable): Promise<Recipe[]> {
    return HttpBackend.get(`/coach-program/diet/recipes/favorite`, toJS(params)).then((result) =>
      (result ?? []).map((res: any) => createRecipe(res)),
    );
  },

  filterCombination(params?: RecipeSearchParams): Promise<RecipeFilterCombination> {
    return HttpBackend.get(`/coach-program/diet/recipes/filterCombination`, transformRecipeSearchParams(params)).then(
      (result) => result ?? { diets: [], dishTypes: [], cuisines: [], seasons: [] },
    );
  },
};

export type RecipeFilterCombination = {
  diets: string[];
  dishTypes: string[];
  cuisines: string[];
  seasons: string[];
};
